'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { captureAdClick, forgetAdClick, recordPageView } from '@/lib/attribution';
import { CONSENT_EVENT } from '@/lib/consent';
import { captureReferral } from '@/lib/referral';

/**
 * Records first-touch source and the previous page on every navigation, so a
 * lead or booking request can say which page produced it. Renders nothing and
 * makes no network request: see src/lib/attribution.ts for exactly what is kept.
 *
 * The Google Ads click id is the one item that depends on consent. It is kept only while advertising storage
 * is allowed, picked up again if the visitor accepts on the landing page, and removed if they withdraw.
 */
export function AttributionCapture() {
  const pathname = usePathname();
  useEffect(() => {
    recordPageView(pathname);
    // Partner links (?ref=CODE) can land on any page, so this runs on every navigation.
    captureReferral(pathname);
  }, [pathname]);

  useEffect(() => {
    const onConsent = (e: Event) => {
      const granted = (e as CustomEvent<{ ad_storage?: string }>).detail?.ad_storage === 'granted';
      if (granted) captureAdClick();
      else forgetAdClick();
    };
    window.addEventListener(CONSENT_EVENT, onConsent);
    return () => window.removeEventListener(CONSENT_EVENT, onConsent);
  }, []);

  return null;
}
